/* eslint-disable prefer-destructuring */
import { Constants } from '../utils'

type Query = {
  keyword: string
  operator: string
  value: string
  customField: string
  hasCustomField: boolean
}

let mainOperatorStarters: string[] = []
let mainOperators: string[] = []
let isSetup = false

const withErrorPrefix = (error: string): string => {
  const prefix = 'Syntax is incorrect.'
  return `${prefix} ${error}`
}

const preCheck = (str: string): { isValid: boolean; message: string } => {
  let response = { isValid: true, message: '' }

  if (str.substring(str.length - 5).includes(' AND')) {
    response = {
      isValid: false,
      message: withErrorPrefix(
        'Join operator AND cannot be alone. Query should start with a Property.'
      )
    }
  }

  return response
}

const lex = (value: string): string[] => {
  const newStr = value.replace(/ AND /g, ' and ')
  return newStr.split(' and ')
}

const isOperator = (operator: string): boolean =>
  mainOperators.includes(operator.toLowerCase())

const isValueRequired = (operator: string): boolean =>
  Constants.OperatorsRequireValues.includes(operator.toLowerCase())

const extractCustomField = (
  keyword: string
): { customField: string; keyword: string; hasCustomField: boolean } => {
  let response = { customField: '', keyword, hasCustomField: false }

  if (keyword.includes(':'))
    response = {
      customField: keyword.split(':')[1],
      keyword: keyword.split(':')[0],
      hasCustomField: true
    }

  return response
}

const isValidSyntax = ({
  keyword,
  operator,
  value,
  customField,
  hasCustomField
}: Query): { isValid: boolean; message: string } => {
  const response = { isValid: false, message: '' }

  if (!keyword || keyword === '' || isOperator(keyword)) {
    response.message = withErrorPrefix('Query should start with a Property.')
    return response
  }

  if (!operator || operator === '') {
    response.message = withErrorPrefix(
      'An Operator (ex: EQUAL TO, IN, CONTAINS) is needed after a Property.'
    )
    return response
  }

  if ((!value || value === '') && isValueRequired(operator)) {
    response.message = withErrorPrefix(
      `The operator ${operator} requires a value.`
    )
    return response
  }

  if (hasCustomField && !customField) {
    response.message = withErrorPrefix(`No custom field name was provided.`)
    return response
  }

  return { isValid: true, message: '' }
}

const getOperator = (str: string): { label: string; value: string } => {
  const operator: ReturnType<typeof getOperator> = { label: '', value: '' }
  let lastIndex = -1

  mainOperators.forEach(op => {
    const regex = new RegExp(`(\\s)${op.toLowerCase()}($|\\s)`, 'g')
    const indexAt = str.toLowerCase().search(regex)

    if (
      (indexAt !== -1 && lastIndex === -1) ||
      (indexAt !== -1 && lastIndex !== -1 && indexAt <= lastIndex)
    ) {
      operator.value = op
      operator.label = str.substring(indexAt + 1, indexAt + op.length + 1)
      lastIndex = indexAt
    }
  })

  return operator
}

const getOtherProps = (
  str: string,
  operator: string,
  end = '\\s'
): string[] => {
  const regex = new RegExp(`\\s${operator}${end}(.*)`, 'g')
  return str.split(regex)
}

export const setup = (
  starters: string[] = [],
  operators: string[] = []
): void => {
  mainOperatorStarters = starters.length > 0 ? starters : Constants.Starters
  mainOperators = operators.length > 0 ? operators : Constants.Operators
  isSetup = true
}

export const parser = (
  str: string
): { successful: boolean; data: Query[]; error: null | string } => {
  if (!isSetup) {
    setup()
  }

  const queries: Query[] = []
  const response: ReturnType<typeof parser> = {
    successful: false,
    data: [],
    error: null
  }

  try {
    const precheckResult = preCheck(str)

    if (!precheckResult.isValid) {
      throw new Error(precheckResult.message)
    }

    const lexed = lex(str)

    lexed.forEach(currentQuery => {
      const operator = getOperator(currentQuery)
      let field = currentQuery
      let value = ''

      if (operator.value) {
        // If there are values after operator
        let props = getOtherProps(currentQuery, operator.label)

        // If there are no values after operator
        if (props.length === 1) {
          props = getOtherProps(currentQuery, operator.label, '$')
        }

        ;[field, /* istanbul ignore next */ value = ''] = props
      }

      const query = {
        ...extractCustomField(field),
        operator: operator.label,
        value
      }

      queries.push(query)
      const validationResult = isValidSyntax(query)

      if (!validationResult.isValid) {
        throw new Error(validationResult.message)
      }
    })

    response.successful = true
  } catch (error) {
    response.error = (error as Error).message
  }

  response.data = queries
  return response
}
